import { Form, Input, InputNumber, Modal, Select, message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { serverUrl } from "../../../../../nestserver";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const CATEGORIES = [
  "Stationery",
  "Furniture",
  "Cleaning",
  "Sports",
  "Lab",
  "Electronics",
  "Other",
];

const UNITS = ["piece", "box", "packet", "set", "pair", "kg", "litre", "metre"];

/** Create or edit one catalogue entry. */
const AddEditStockItem = ({ open, onClose, onSaved, item }) => {
  const [form] = Form.useForm();
  const isEdit = !!item;
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      form.setFieldsValue({
        name: item.name,
        category: item.category,
        unit: item.unit,
        quantity_in_hand: item.quantity_in_hand,
        low_stock_threshold: item.low_stock_threshold,
        description: item.description,
      });
    } else {
      form.resetFields();
    }
  }, [open, isEdit, item, form]);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        // Quantity is moved with the Adjust action, never silently overwritten
        // by an edit — otherwise a stale form would clobber a concurrent issue.
        const { quantity_in_hand, ...rest } = values;
        await axios.patch(`${serverUrl}/school-stock/items/${item._id}`, rest, {
          headers: authHeader(),
        });
        message.success("Stock item updated");
      } else {
        await axios.post(`${serverUrl}/school-stock/items`, values, {
          headers: authHeader(),
        });
        message.success("Stock item added");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Stock Item" : "New Stock Item"}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEdit ? "Save" : "Add item"}
      confirmLoading={saving}
      width={620}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        className="pt-4"
        initialValues={{ unit: "piece", quantity_in_hand: 0, low_stock_threshold: 0 }}
      >
        <Form.Item
          name="name"
          label="Item name"
          rules={[{ required: true, message: "Enter an item name" }]}
        >
          <Input placeholder="e.g. Chalk (box)" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="category" label="Category">
            <Select
              allowClear
              placeholder="Select a category"
              options={CATEGORIES.map((c) => ({ label: c, value: c }))}
            />
          </Form.Item>

          <Form.Item
            name="unit"
            label="Unit"
            rules={[{ required: true, message: "Pick a unit" }]}
          >
            <Select
              showSearch
              options={UNITS.map((u) => ({ label: u, value: u }))}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {!isEdit && (
            <Form.Item
              name="quantity_in_hand"
              label="Opening quantity"
              rules={[{ required: true, message: "Enter the opening quantity" }]}
            >
              <InputNumber className="w-full" min={0} precision={0} />
            </Form.Item>
          )}

          <Form.Item
            name="low_stock_threshold"
            label="Low stock alert at"
            tooltip="The item is flagged as low stock once quantity falls to or below this."
          >
            <InputNumber className="w-full" min={0} precision={0} />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Optional notes about this item" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditStockItem;
